import { useCallback, useEffect, useRef } from 'react';
import { getEditor, hasSiteContextPanels } from 'agentable-canvas/whiteboard';
import { useCanvasSession } from '../contexts/CanvasSessionContext';
import { waitForCanvasRestoreResolved } from '../lib/canvas-restore-events';
import { openSiteOnCanvas, type OpenSiteOnCanvasOptions } from '../lib/open-site-on-canvas';

export const CANVAS_OPEN_SITE_EVENT = 'landi:open-site-on-canvas';

export interface CanvasOpenSiteEventDetail {
  siteId: string;
  agencyId?: string | null;
  siteLabel?: string;
  focusPreview?: boolean;
}

interface PendingSiteOpen {
  siteId: string;
  options: OpenSiteOnCanvasOptions;
  force: boolean;
}

function waitForEditorReady(timeoutMs: number = 8_000): Promise<boolean> {
  if (getEditor()) {
    return Promise.resolve(true);
  }
  return new Promise<boolean>((resolve) => {
    let attempts = 0;
    const maxAttempts = Math.ceil(timeoutMs / 100);
    const timer = window.setInterval(() => {
      attempts += 1;
      if (getEditor()) {
        window.clearInterval(timer);
        resolve(true);
        return;
      }
      if (attempts >= maxAttempts) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });
}

async function snapshotHasRenderablePanels(siteId: string): Promise<boolean> {
  const editorReady = await waitForEditorReady();
  if (!editorReady) {
    return false;
  }
  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });
  const editor = getEditor();
  if (!editor) {
    return false;
  }
  return hasSiteContextPanels(editor, siteId);
}

const SITE_OPEN_PHASES = new Set(['complete', 'editing']);

/**
 * Opens grouped site panels when `landi:open-site-on-canvas` fires or when
 * the session enters a site-workspace phase — after snapshot restore
 * resolves so a late restore cannot wipe freshly opened panels.
 */
export function useSitePanelsBridge(): void {
  const { session } = useCanvasSession();
  const openedSiteRef = useRef<string | null>(null);
  const pendingOpenRef = useRef<PendingSiteOpen | null>(null);
  const openGenerationRef = useRef(0);
  const activeCleanupRef = useRef<(() => void) | undefined>(undefined);

  const cancelActiveOpen = useCallback((): void => {
    openGenerationRef.current += 1;
    pendingOpenRef.current = null;
    activeCleanupRef.current?.();
    activeCleanupRef.current = undefined;
  }, []);

  const scheduleSiteOpen = useCallback((pending: PendingSiteOpen): (() => void) => {
    cancelActiveOpen();
    const generation = openGenerationRef.current;
    pendingOpenRef.current = pending;

    void (async () => {
      const hadSnapshot = await waitForCanvasRestoreResolved(pending.siteId);
      if (generation !== openGenerationRef.current) {
        return;
      }
      if (pendingOpenRef.current?.siteId !== pending.siteId) {
        return;
      }
      if (!pending.force && openedSiteRef.current === pending.siteId) {
        return;
      }
      if (hadSnapshot) {
        // Let snapshot restore settle (loads any freehand/other shapes) before
        // (re)establishing the site panel layout below.
        await snapshotHasRenderablePanels(pending.siteId);
        if (generation !== openGenerationRef.current) {
          return;
        }
      }

      // Always (re)apply the deterministic default site layout on open so a
      // freshly opened site group includes chat docked flush-left at full
      // height — even when a saved snapshot (which may predate chat-by-default)
      // was restored. openSiteOnCanvas reuses the singleton panel shapes, so
      // this normalizes rather than duplicating panels.
      activeCleanupRef.current = openSiteOnCanvas(pending.options);
      openedSiteRef.current = pending.siteId;
      pendingOpenRef.current = null;
    })();

    return cancelActiveOpen;
  }, [cancelActiveOpen]);

  useEffect(() => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<CanvasOpenSiteEventDetail>).detail;
      if (!detail?.siteId) return;
      scheduleSiteOpen({
        siteId: detail.siteId,
        force: true,
        options: {
          siteId: detail.siteId,
          agencyId: detail.agencyId,
          siteLabel: detail.siteLabel,
          focusPreview: detail.focusPreview,
          session,
        },
      });
    };

    window.addEventListener(CANVAS_OPEN_SITE_EVENT, handler);
    return () => window.removeEventListener(CANVAS_OPEN_SITE_EVENT, handler);
  }, [scheduleSiteOpen, session]);

  useEffect(() => {
    const siteId = session.siteId;
    if (!siteId || !SITE_OPEN_PHASES.has(session.phase)) {
      return;
    }
    if (openedSiteRef.current === siteId) {
      return;
    }
    return scheduleSiteOpen({
      siteId,
      force: false,
      options: { siteId, session },
    });
  }, [session.siteId, session.phase, session.updatedAt, session.generationSessionId, session.artifacts, session, scheduleSiteOpen]);
}

export function dispatchOpenSiteOnCanvas(detail: CanvasOpenSiteEventDetail): void {
  window.dispatchEvent(
    new CustomEvent<CanvasOpenSiteEventDetail>(CANVAS_OPEN_SITE_EVENT, {
      detail,
      bubbles: true,
      composed: true,
    }),
  );
}

/** Mount helper — call from App shell. */
export function SitePanelsBridgeMount(): null {
  useSitePanelsBridge();
  return null;
}
